import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Typography,
  Card,
  CardContent,
  IconButton,
  useTheme,
  CircularProgress,
  Alert,
  Paper,
  Container,
  alpha,
  Stack,
} from '@mui/material';
import {
  Apps as AppsIcon,
  Group as GroupIcon,
  People as PeopleIcon,
  Update as UpdateIcon,
  TrendingUp as TrendingUpIcon,
  CloudDownload as CloudDownloadIcon,
  DashboardRounded as DashboardIcon,
} from '@mui/icons-material';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip as ChartTooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  Legend,
} from 'recharts';
import { appsApi, groupsApi, usersApi } from '../services/api';

const StatCard = ({ title, value, icon, loading, error }) => {
  const theme = useTheme();
  
  return (
    <Card
      elevation={0}
      sx={{
        height: '100%',
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
        borderRadius: 2,
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: `0 4px 24px ${alpha(theme.palette.primary.main, 0.15)}`,
        }
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box display="flex" alignItems="center" mb={2}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: theme => alpha(theme.palette.primary.main, 0.1),
              color: 'primary.main',
            }}
          >
            {React.cloneElement(icon, { sx: { fontSize: 24 } })}
          </Box>
          <Box ml={2}>
            <Typography variant="body1" color="text.secondary" gutterBottom>
              {title}
            </Typography>
            {loading ? (
              <CircularProgress size={24} />
            ) : error ? (
              <Typography color="error" variant="body2">Error loading data</Typography>
            ) : (
              <Typography variant="h4" fontWeight="bold">
                {value}
              </Typography>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

const Dashboard = () => {
  const [stats, setStats] = useState({
    totalApps: 0,
    totalGroups: 0,
    totalUsers: 0,
    activeUsers: 0,
    recentUpdates: 0,
    totalDownloads: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [groupData, setGroupData] = useState([]);
  const [recentAppsData, setRecentAppsData] = useState([]);
  const [appDownloadsData, setAppDownloadsData] = useState([]);
  const [userActivityData, setUserActivityData] = useState([]);
  const theme = useTheme();

  const COLORS = [
    theme.palette.primary.main,
    theme.palette.secondary.main,
    theme.palette.success.main,
    theme.palette.warning.main,
    theme.palette.error.main,
  ];

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch all required data
        const [appsRes, groupsRes, usersRes] = await Promise.all([
          appsApi.getAllApps(),
          groupsApi.getGroups(),
          usersApi.getAllUsers()
        ]);
        
        // Extract data from responses
        const apps = appsRes?.data || [];
        const groups = Array.isArray(groupsRes?.data) ? groupsRes.data : (groupsRes?.data?.groups || []);
        const users = Array.isArray(usersRes?.data) ? usersRes.data : (usersRes?.data?.users || []);

        // Process apps data
        const totalApps = apps.length;
        const totalDownloads = apps.reduce((sum, app) => sum + (parseInt(app.amountOfUsers) || 0), 0);
        
        // Process recent updates
        const recentApps = apps
          .filter(app => app.updatedAt)
          .map(app => ({
            name: app.name,
            daysSinceUpdate: Math.floor((new Date() - new Date(app.updatedAt)) / (1000 * 60 * 60 * 24)),
            updatedAt: new Date(app.updatedAt).toLocaleDateString(),
          }))
          .sort((a, b) => a.daysSinceUpdate - b.daysSinceUpdate)
          .slice(0, 5);

        // Process groups and users data
        const totalGroups = groups.length;
        const totalUsers = users.length;
        
        // Calculate active users (users who have logged in within last 30 days)
        const activeUsers = users.filter(user => {
          if (!user.lastLoginAt) return false;
          const lastLogin = new Date(user.lastLoginAt);
          const thirtyDaysAgo = new Date();
          thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
          return lastLogin >= thirtyDaysAgo;
        }).length;

        // Prepare group data for chart
        const groupStats = groups
          .map(group => ({
            name: group.groupName || group.groupTag,
            users: users.filter(user => user.groupTag === group.groupTag).length
          }))
          .sort((a, b) => b.users - a.users)
          .slice(0, 5); // Show top 5 groups

        // Process apps data for downloads chart
        const appDownloads = apps
          .map(app => ({
            name: app.name,
            downloads: parseInt(app.amountOfUsers) || 0,
            users: parseInt(app.amountOfUsers) || 0,
          }))
          .sort((a, b) => b.downloads - a.downloads)
          .slice(0, 5);
        
        setAppDownloadsData(appDownloads);

        // Process user activity data
        const activeUsersCount = users.filter(user => {
          if (!user.lastLoginAt) return false;
          const lastLogin = new Date(user.lastLoginAt);
          const thirtyDaysAgo = new Date();
          thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
          return lastLogin >= thirtyDaysAgo;
        }).length;

        const userActivity = [
          { name: 'Active Users', value: activeUsersCount },
          { name: 'Inactive Users', value: users.length - activeUsersCount },
        ];

        setUserActivityData(userActivity);

        setStats({
          totalApps,
          totalGroups,
          totalUsers,
          activeUsers,
          recentUpdates: recentApps.length,
          totalDownloads
        });
        setGroupData(groupStats);
        setRecentAppsData(recentApps);
      } catch (err) {
        console.error('Error fetching dashboard data:', err);
        setError('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  return (
    <Container maxWidth={false}>
      {/* Header */}
      <Paper 
        elevation={0}
        sx={{ 
          p: 3, 
          mb: 3, 
          background: theme => `linear-gradient(45deg, ${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
          borderRadius: 3,
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={2}>
            <DashboardIcon sx={{ fontSize: 40, color: 'primary.main' }} />
            <Box>
              <Typography variant="h4" fontWeight="bold" gutterBottom>
                Dashboard
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Overview of your apps, groups, and user statistics
              </Typography>
            </Box>
          </Box>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
      )}

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Total Apps"
            value={stats.totalApps}
            icon={<AppsIcon />}
            loading={loading}
            error={error}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Total Groups"
            value={stats.totalGroups}
            icon={<GroupIcon />}
            loading={loading}
            error={error}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Total Users"
            value={stats.totalUsers}
            icon={<PeopleIcon />}
            loading={loading}
            error={error}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Active Users"
            value={stats.activeUsers}
            icon={<TrendingUpIcon />}
            loading={loading}
            error={error}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Recent Updates"
            value={stats.recentUpdates}
            icon={<UpdateIcon />}
            loading={loading}
            error={error}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Total Downloads"
            value={stats.totalDownloads}
            icon={<CloudDownloadIcon />}
            loading={loading}
            error={error}
          />
        </Grid>
      </Grid>

      {/* Charts Section */}
      <Grid container spacing={3}>
        {/* Group Distribution Chart */}
        <Grid item xs={12} md={6}>
          {groupData.length > 0 && (
            <Paper
              variant="outlined"
              sx={{
                p: 3,
                borderRadius: 2,
                borderColor: theme => alpha(theme.palette.primary.main, 0.2),
                height: 400,
              }}
            >
              <Typography variant="h6" fontWeight="bold" gutterBottom color="primary">
                Top Groups by Users
              </Typography>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={groupData}
                  margin={{
                    top: 20,
                    right: 30,
                    left: 20,
                    bottom: 60,
                  }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.1)} />
                  <XAxis
                    dataKey="name"
                    angle={-45}
                    textAnchor="end"
                    height={60}
                    tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
                  />
                  <YAxis tick={{ fill: theme.palette.text.secondary }} />
                  <ChartTooltip />
                  <Bar
                    dataKey="users"
                    fill={theme.palette.primary.main}
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          )}
        </Grid>

        {/* Recent App Updates Chart */}
        <Grid item xs={12} md={6}>
          {recentAppsData.length > 0 && (
            <Paper
              variant="outlined"
              sx={{
                p: 3,
                borderRadius: 2,
                borderColor: theme => alpha(theme.palette.primary.main, 0.2),
                height: 400,
              }}
            >
              <Typography variant="h6" fontWeight="bold" gutterBottom color="primary">
                Recently Updated Apps
              </Typography>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={recentAppsData}
                  margin={{
                    top: 20,
                    right: 30,
                    left: 20,
                    bottom: 60,
                  }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.1)} />
                  <XAxis
                    dataKey="name"
                    angle={-45}
                    textAnchor="end"
                    height={60}
                    tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
                  />
                  <YAxis 
                    tick={{ fill: theme.palette.text.secondary }}
                    label={{ 
                      value: 'Days Since Update', 
                      angle: -90, 
                      position: 'insideLeft',
                      style: { fill: theme.palette.text.secondary }
                    }}
                  />
                  <ChartTooltip 
                    formatter={(value, name, props) => [
                      `${value} days ago (${props.payload.updatedAt})`,
                      'Last Update'
                    ]}
                  />
                  <Bar
                    dataKey="daysSinceUpdate"
                    fill={theme.palette.secondary.main}
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          )}
        </Grid>

        {/* App Downloads Chart */}
        <Grid item xs={12} md={6}>
          {appDownloadsData.length > 0 && (
            <Paper
              variant="outlined"
              sx={{
                p: 3,
                borderRadius: 2,
                borderColor: theme => alpha(theme.palette.primary.main, 0.2),
                height: 400,
              }}
            >
              <Typography variant="h6" fontWeight="bold" gutterBottom color="primary">
                Top Apps by Downloads
              </Typography>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={appDownloadsData}
                  margin={{
                    top: 20,
                    right: 30,
                    left: 20,
                    bottom: 60,
                  }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.1)} />
                  <XAxis
                    dataKey="name"
                    angle={-45}
                    textAnchor="end"
                    height={60}
                    tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
                  />
                  <YAxis 
                    yAxisId="left"
                    tick={{ fill: theme.palette.text.secondary }}
                    label={{ 
                      value: 'Downloads', 
                      angle: -90, 
                      position: 'insideLeft',
                      style: { fill: theme.palette.text.secondary }
                    }}
                  />
                  <YAxis 
                    yAxisId="right"
                    orientation="right"
                    tick={{ fill: theme.palette.text.secondary }}
                    label={{ 
                      value: 'Active Users', 
                      angle: 90, 
                      position: 'insideRight',
                      style: { fill: theme.palette.text.secondary }
                    }}
                  />
                  <ChartTooltip />
                  <Legend />
                  <Line
                    yAxisId="left"
                    type="monotone"
                    dataKey="downloads"
                    stroke={theme.palette.primary.main}
                    strokeWidth={2}
                    dot={{ fill: theme.palette.primary.main }}
                  />
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="users"
                    stroke={theme.palette.secondary.main}
                    strokeWidth={2}
                    dot={{ fill: theme.palette.secondary.main }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Paper>
          )}
        </Grid>

        {/* User Activity Chart */}
        <Grid item xs={12} md={6}>
          {userActivityData.length > 0 && (
            <Paper
              variant="outlined"
              sx={{
                p: 3,
                borderRadius: 2,
                borderColor: theme => alpha(theme.palette.primary.main, 0.2),
                height: 400,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Typography variant="h6" fontWeight="bold" gutterBottom color="primary">
                User Activity Distribution
              </Typography>
              <Box sx={{ flex: 1, minHeight: 0 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={userActivityData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      paddingAngle={5}
                      dataKey="value"
                      label={({
                        cx,
                        cy,
                        midAngle,
                        innerRadius,
                        outerRadius,
                        value,
                        index
                      }) => {
                        const RADIAN = Math.PI / 180;
                        const radius = 25 + innerRadius + (outerRadius - innerRadius);
                        const x = cx + radius * Math.cos(-midAngle * RADIAN);
                        const y = cy + radius * Math.sin(-midAngle * RADIAN);
                        return (
                          <text
                            x={x}
                            y={y}
                            fill={theme.palette.text.secondary}
                            textAnchor={x > cx ? 'start' : 'end'}
                            dominantBaseline="central"
                          >
                            {`${userActivityData[index].name} (${value})`}
                          </text>
                        );
                      }}
                    >
                      {userActivityData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <ChartTooltip />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
              <Stack
                direction="row"
                spacing={2}
                justifyContent="center"
                sx={{ mt: 2 }}
              >
                {userActivityData.map((entry, index) => (
                  <Box
                    key={entry.name}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: 0.5,
                        bgcolor: COLORS[index % COLORS.length],
                      }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      {entry.name}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Paper>
          )}
        </Grid>
      </Grid>
    </Container>
  );
};

export default Dashboard;
